import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getUserProfile, getSystemRolesPermissions } from '../services/auth';

/**
 * usePermissions — Centralized RBAC hook.
 *
 * Reads the current user's role keys from `profiles.roles`,
 * fetches the corresponding `system_roles.permissions`,
 * and additively merges them (most permissive wins).
 *
 * Usage:
 *   const { can, canRead, canWrite, canDelete, loading } = usePermissions();
 *   if (canWrite('hotels')) { ... }
 */
export default function usePermissions() {
  const { session } = useSelector((state) => state.auth);
  const userId = session?.user?.id;

  const [mergedPermissions, setMergedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [userRoleKeys, setUserRoleKeys] = useState([]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPermissions = async () => {
      try {
        // 1. Get user's role keys from profiles
        const { data: profile, error: profileError } = await getUserProfile(userId);

        if (profileError || !profile) {
          console.error('usePermissions: Failed to fetch profile roles', profileError);
          if (!cancelled) setLoading(false);
          return;
        }

        const roleKeys = profile.roles || ['sales'];
        if (!cancelled) setUserRoleKeys(roleKeys);

        // 2. Fetch permissions for each role
        const { data: rolesData, error: rolesError } = await getSystemRolesPermissions(roleKeys);

        if (rolesError) {
          console.error('usePermissions: Failed to fetch system_roles', rolesError);
          if (!cancelled) setLoading(false);
          return;
        }

        // 3. Additive merge: if ANY role grants a permission, the user has it
        const merged = {};
        for (const role of (rolesData || [])) {
          const perms = role.permissions || {};
          for (const [moduleKey, actions] of Object.entries(perms)) {
            if (!merged[moduleKey]) merged[moduleKey] = {};
            for (const [action, value] of Object.entries(actions)) {
              if (value === true) {
                merged[moduleKey][action] = true;
              }
            }
          }
        }

        if (!cancelled) {
          setMergedPermissions(merged);
          setLoading(false);
        }
      } catch (err) {
        console.error('usePermissions: Unexpected error', err);
        if (!cancelled) setLoading(false);
      }
    };

    fetchPermissions();
    return () => { cancelled = true; };
  }, [userId]);

  /**
   * Check if the user has a specific permission on a module.
   */
  const can = useCallback((moduleKey, action) => {
    // Admin role key always has full access
    if (userRoleKeys.includes('admin')) return true;
    return mergedPermissions[moduleKey]?.[action] === true;
  }, [mergedPermissions, userRoleKeys]);

  const canRead = useCallback((moduleKey) => can(moduleKey, 'read'), [can]);
  const canWrite = useCallback((moduleKey) => can(moduleKey, 'write'), [can]);
  const canDelete = useCallback((moduleKey) => can(moduleKey, 'delete'), [can]);
  const canApprove = useCallback((moduleKey) => can(moduleKey, 'approve'), [can]);

  return {
    can,
    canRead,
    canWrite,
    canDelete,
    canApprove,
    loading,
    isAdmin: userRoleKeys.includes('admin'),
    userRoleKeys,
  };
}
